// eslint-disable-next-line import/no-unresolved
import 'form-motor-es/theme'
import * as BrowserIdentification from 'DTSLib/BrowserIdentification'
import * as Env from './FormMotorEsLib/Env'
import * as Turnstile from 'DTSLib/Turnstile'
import { DynamicFlagsFetchingMode } from '../project-conf'
import { GoogleTagManager } from 'DTSLib/Http/GoogleTagManager'
import { LaunchDarkly } from 'DTSLib/Http/LaunchDarkly'
import { Userpilot } from 'userpilot'
import { onDOMReady } from 'DTSLib/Browser/Dom/Extra'
import Logger from 'DTSLib/Logger'

init()

/**
 * Init function (let's avoid global scope pollution)
 */
function init(): void {
  /**
   * Let's start early with a non-blocking import of the application chunk
   */
  const appLoaderModulePromise = import('form-motor-es/AppLoader')

  const logger = new Logger({
    datadogConfiguration: {
      allowedTracingUrls: [
        {
          match: Env.BACKEND_API_URL,
          propagatorTypes: ['datadog', 'tracecontext'],
        },
      ],
      applicationId: Env.DATADOG_APPLICATION_ID,
      clientToken: Env.DATADOG_CLIENT_TOKEN,
      enableSessionTracking: false,
      env: Env.BUILD_ENV,
      isEnabled: Env.IS_DATADOG_ENABLED,
      proxy: undefined,
      service: Env.DATADOG_SERVICE_NAME,
      version: Env.VERSION,
    },
    isPrivateLogEnvironment: Env.IS_PRIVATE_LOG_ENVIRONMENT,
  })
  Turnstile.init({
    isPrerenderBrowser: BrowserIdentification.isPreRendererUserAgent(),
    logger: logger,
  })

  loadCacheServiceWorker(logger)
  backCacheReloadListener(logger)

  const launchDarkly = new LaunchDarkly({
    clientId: Env.LAUNCHDARKLY_CLIENT_ID,
    context: {
      isMobile: BrowserIdentification.isMobileUserAgent(),
      key: BrowserIdentification.getPersistentIdentifier(
        Env.IS_CROSS_DOMAIN_COOKIE_ENABLED,
        '.helloprima.es'
      ),
      kind: 'user',
    },
    flagsFetchingMode: Env.IS_DYNAMIC_FLAGS_FETCHING_ENABLED
      ? DynamicFlagsFetchingMode.remote
      : DynamicFlagsFetchingMode.disabled,
    logHitSending: !Env.IS_PRIVATE_LOG_ENVIRONMENT,
    logger: logger,
  })

  Userpilot.initialize(Env.USERPILOT_TOKEN)
  Userpilot.identify(
    BrowserIdentification.getPersistentIdentifier(
      Env.IS_CROSS_DOMAIN_COOKIE_ENABLED,
      '.helloprima.es'
    )
  )

  const gTM = new GoogleTagManager({
    authCode: Env.GTM_AUTH_CODE,
    consentModeSettings: {
      // Reference to this before changing the base consent mode: https://www.notion.so/helloprima/Overview-of-Cookie-Management-at-Prima-e612cd6e87aa4832b79fc128e6dfdd46#a8a6f60ca7fe4eccbae987ea2ecedd7e
      defaultConsent: {
        adPersonalization: 'denied',
        adStorage: 'denied',
        adUserData: 'denied',
        analyticsStorage: 'denied',
        functionalityStorage: 'denied',
        personalizationStorage: 'denied',
        securityStorage: 'granted',
      },
      isEnabled: true,
    },
    isPreRendering: BrowserIdentification.isPreRendererUserAgent(),
    logger: logger,
    previewId: Env.GTM_PREVIEW_ID,
    trackingId: Env.GTM_ID,
  })

  /**
   * Before starting Elm application we need the application chunk resolved and DOM ready
   */
  Promise.all([appLoaderModulePromise, onDOMReady])
    .then(async ([appLoaderModule]) => {
      const flagsVal = await launchDarkly.getFlag('dts-form-motor-es-fe-configuration', {})
      logger.devConsoleLog(
        'index.ts AppLoader initialization',
        'Remote Dynamic flags fetching enabled: ',
        flagsVal
      )
      new appLoaderModule.AppLoader({
        applicantIdentifier: BrowserIdentification.getPersistentIdentifier(
          Env.IS_CROSS_DOMAIN_COOKIE_ENABLED,
          '.helloprima.es'
        ),
        applicantSessionIdentifier: BrowserIdentification.getSessionIdentifier(
          Env.IS_CROSS_DOMAIN_COOKIE_ENABLED,
          '.helloprima.es'
        ),
        dynamicFlags: flagsVal,
        googleTagManagerInstance: gTM,
        isMobile: BrowserIdentification.isMobileUserAgent(),
        isPreRendererBrowser: BrowserIdentification.isPreRendererUserAgent(),
        launchDarklyInstance: launchDarkly,
        logger: logger,
        processEnv: process.env,
      })
    })
    .catch((err) => {
      logger.devConsoleError('index.ts:initial promise catastrophic error', err)

      // Desperate attempt to make it work again
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    })
}

/**
 * Loads cache service worker (aka Google Workbox).
 * service-worker.js is automatically built by webpack and automatically tracks outputted files with their build hash
 * Check https://webpack.js.org/guides/progressive-form-motor-itlication/ to learn more
 *
 * @param logger - logger instance
 */
function loadCacheServiceWorker(logger: Logger): void {
  const isEnabled = Env.IS_PRODUCTION_BUILD_ENV || Env.IS_STAGING_BUILD_ENV
  if ('serviceWorker' in navigator && isEnabled) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          logger.devConsoleLog('index.ts:loadCacheServiceWorker', registration)
        })
        .catch((registrationError) => {
          // We can’t do much to handle these cases as they depend on the browser setup or potential page reloads during the SW registration.
          logger.devConsoleLog('index.ts:loadCacheServiceWorker', registrationError)
        })
    })
  }

  if (!isEnabled) {
    logger.devConsoleLog(
      'index.ts:loadCacheServiceWorker',
      'Service worker is not enabled outside production and staging environment'
    )
  }
}

/**
 * Forces page reload in case of cross-domain back click.
 * In some browser the application state cache is resumed from the very last moment in some others no,
 * this will fix the "show loader" on some browser when you're navigating outside the SPA scope and then go back
 *
 * @param logger - logger instance
 */
function backCacheReloadListener(logger: Logger): void {
  window.onpageshow = function (event): void {
    if (event.persisted) {
      logger.devConsoleLog('Back cache reload detected')
      window.location.reload()
    }
  }
}
